/**
 * Utility functions for sorting resume sections by date
 * Used for work experience, education, and certifications
 */

/**
 * Parse a date string and convert it to a comparable Date object
 * Handles various date formats commonly found in resumes
 * @param {string} dateStr - Date string (e.g., "2023", "Jan 2023", "2023-01", "Present")
 * @returns {Date} - Date object for comparison
 */
export const parseResumeDate = (dateStr) => {
  if (!dateStr) return new Date(0); // Very old date for missing dates

  const cleanDate = dateStr.toString().trim().toLowerCase();

  // Handle "present", "current", "ongoing" cases
  if (cleanDate === "present" || cleanDate === "current" || cleanDate === "ongoing") {
    return new Date(); // Current date
  }

  // Handle year-only format (e.g., "2023")
  if (/^\d{4}$/.test(cleanDate)) {
    return new Date(parseInt(cleanDate), 0, 1); // January 1st of that year
  }

  // Handle month-year formats (e.g., "Jan 2023", "January 2023", "01/2023", "2023-01")
  const monthYearPatterns = [
    /^(\w+)\s+(\d{4})$/, // "Jan 2023", "January 2023"
    /^(\d{1,2})\/(\d{4})$/, // "01/2023"
    /^(\d{4})-(\d{1,2})$/, // "2023-01"
    /^(\d{1,2})-(\d{4})$/, // "01-2023"
  ];

  for (const pattern of monthYearPatterns) {
    const match = cleanDate.match(pattern);
    if (match) {
      const [, part1, part2] = match;

      // Determine which part is month and which is year
      let month, year;

      if (pattern === monthYearPatterns[0]) {
        // Month name format
        month = parseMonthName(part1);
        year = parseInt(part2);
      } else if (pattern === monthYearPatterns[2]) {
        // YYYY-MM format
        year = parseInt(part1);
        month = parseInt(part2) - 1; // Month is 0-indexed in JavaScript
      } else {
        // MM/YYYY or MM-YYYY format
        month = parseInt(part1) - 1; // Month is 0-indexed in JavaScript
        year = parseInt(part2);
      }

      if (isValidMonth(month) && isValidYear(year)) {
        return new Date(year, month, 1);
      }
    }
  }

  // Try to parse as a standard date
  const standardDate = new Date(dateStr);
  if (!isNaN(standardDate.getTime())) {
    return standardDate;
  }

  // If all parsing fails, return a very old date
  console.warn(`Could not parse date: ${dateStr}`);
  return new Date(0);
};

/**
 * Parse month name to month index (0-11)
 * @param {string} monthName - Month name (e.g., "Jan", "January")
 * @returns {number} - Month index (0-11)
 */
const parseMonthName = (monthName) => {
  const monthNames = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
  };

  return monthNames[monthName.toLowerCase()] ?? 0;
};

/**
 * Check if month is valid (0-11)
 * @param {number} month - Month index
 * @returns {boolean} - Is valid month
 */
const isValidMonth = (month) => {
  return month >= 0 && month <= 11;
};

/**
 * Check if year is valid (reasonable range)
 * @param {number} year - Year
 * @returns {boolean} - Is valid year
 */
const isValidYear = (year) => {
  const currentYear = new Date().getFullYear();
  return year >= 1900 && year <= currentYear + 10; // Allow future dates up to 10 years
};

/**
 * Sort work experience entries by date (most recent first)
 * @param {Array} experiences - Array of experience objects
 * @returns {Array} - Sorted array of experiences
 */
export const sortExperienceByDate = (experiences) => {
  if (!Array.isArray(experiences)) return experiences;

  return [...experiences].sort((a, b) => {
    // Get end dates (or start dates if no end date)
    const aEndDate = parseResumeDate(a.endDate || a.startDate);
    const bEndDate = parseResumeDate(b.endDate || b.startDate);

    // Sort in descending order (most recent first)
    if (aEndDate.getTime() !== bEndDate.getTime()) {
      return bEndDate.getTime() - aEndDate.getTime();
    }

    // If end dates are the same, sort by start date (most recent first)
    const aStartDate = parseResumeDate(a.startDate);
    const bStartDate = parseResumeDate(b.startDate);
    return bStartDate.getTime() - aStartDate.getTime();
  });
};

/**
 * Sort education entries by date (most recent first)
 * @param {Array} education - Array of education objects
 * @returns {Array} - Sorted array of education
 */
export const sortEducationByDate = (education) => {
  if (!Array.isArray(education)) return education;

  return [...education].sort((a, b) => {
    // Get end dates (graduation dates, or start dates if no end date)
    const aEndDate = parseResumeDate(a.endDate || a.graduationDate || a.startDate);
    const bEndDate = parseResumeDate(b.endDate || b.graduationDate || b.startDate);

    // Sort in descending order (most recent first)
    if (aEndDate.getTime() !== bEndDate.getTime()) {
      return bEndDate.getTime() - aEndDate.getTime();
    }

    // If end dates are the same, sort by start date (most recent first)
    const aStartDate = parseResumeDate(a.startDate);
    const bStartDate = parseResumeDate(b.startDate);
    return bStartDate.getTime() - aStartDate.getTime();
  });
};

/**
 * Sort certifications by date (most recent first)
 * @param {Array} certifications - Array of certification objects
 * @returns {Array} - Sorted array of certifications
 */
export const sortCertificationsByDate = (certifications) => {
  if (!Array.isArray(certifications)) return certifications;

  return [...certifications].sort((a, b) => {
    // Get certification dates (use date, issuedDate, or any date field)
    const aDate = parseResumeDate(a.date || a.issuedDate || a.issueDate || a.certificationDate);
    const bDate = parseResumeDate(b.date || b.issuedDate || b.issueDate || b.certificationDate);

    // Sort in descending order (most recent first)
    return bDate.getTime() - aDate.getTime();
  });
};

/**
 * Sort all sections in resume data by date
 * @param {Object} resumeData - Resume data object
 * @returns {Object} - Resume data with sorted sections
 */
export const sortAllSectionsByDate = (resumeData) => {
  if (!resumeData || typeof resumeData !== "object") return resumeData;

  const sortedData = { ...resumeData };

  // Sort experience
  if (sortedData.experience) {
    sortedData.experience = sortExperienceByDate(sortedData.experience);
  }

  // Sort education
  if (sortedData.education) {
    sortedData.education = sortEducationByDate(sortedData.education);
  }

  // Sort certifications
  if (sortedData.certifications) {
    sortedData.certifications = sortCertificationsByDate(sortedData.certifications);
  }

  // Preserve skills structure (both flat and categorized formats)
  if (sortedData.skills) {
    // Don't modify skills - preserve both flat arrays and categorized objects
    sortedData.skills = sortedData.skills;
  }

  return sortedData;
};

/**
 * Sort resume data for display with debugging
 * @param {Object} parsedData - Parsed resume data
 * @returns {Object} - Sorted resume data
 */
export const sortResumeForDisplay = (parsedData) => {
  if (!parsedData) return parsedData;

  const sorted = sortAllSectionsByDate(parsedData);

  return sorted;
};
